import React, { useContext, useMemo } from 'react';
import flush from 'just-flush';
import { IconInfoList } from '@stereoagency/ui';
import { useQuery } from 'urql';
import type { ILazyBlocksImage } from '@/types';
import { FindMediaItemDetailsByIdQueryDocument } from '@/gql/client/graphql';
import { useFragment } from '@/gql';
import { MediaItemFieldsFragment } from '@/gql/documents/fragments';
import { useCallToActions } from '@/hooks';
import { LinesContext } from '@/components/LinesContext';
import { parseHtml } from '@/utils';

type Props = {
  data: {
    label?: string
    title: string
    items: {
      icon?: ILazyBlocksImage
      title: string
      description?: string
      callToActions?: number[]
    }[]
  }
};

export default function NvisoIconInfoList({ data: { label, title, items } }: Props) {
  const { enableLines } = useContext(LinesContext);
  const mediaItemIds = useMemo(() => (
    flush(items.map(({ icon }) => icon ? String(icon.id) : null))
  ), [items]);
  const [{ data: mediaItemsQueryResult }] = useQuery({
    query: FindMediaItemDetailsByIdQueryDocument,
    variables: { ids: mediaItemIds.sort() },
  });
  const mediaItemFields = useFragment(MediaItemFieldsFragment, flush(mediaItemsQueryResult?.mediaItems?.nodes ?? []));
  const callToActionIds = useMemo(() => (
    Array.from(items.reduce<Set<number>>((acc, item) => {
      if (Array.isArray(item.callToActions)) {
        item.callToActions?.forEach((id) => acc.add(id));
      }
      return acc;
    }, new Set()))
  ), [items]);
  const callToActionObjects = useCallToActions(callToActionIds, true);

  return (
    <IconInfoList
      enableLines={enableLines}
      label={label}
      title={title}
      items={flush(items.map((item) => {
        const mediaItem = item.icon?.id
          ? mediaItemFields.find((fields) => item.icon!.id === fields.databaseId)
          : null;

        return {
          title: item.title,
          description: item.description ? parseHtml(item.description) : null,
          icon: mediaItem ? {
            src: mediaItem.mediaItemUrl ?? '',
            width: mediaItem.mediaDetails?.width ?? 100,
            height: mediaItem.mediaDetails?.height ?? 100,
          } : undefined,
          callToActions: Array.isArray(item.callToActions) ? flush(item.callToActions?.map((ctaId) => (
            callToActionObjects.get(ctaId)
          )) ?? []) : [],
        };
      }))}
    />
  );
}
